import React from "react";
import { motion } from "framer-motion";
import { IMAGES } from "../lib/data";

const Necessity = () => {
  return (
    <section
      id="necessity"
      data-testid="necessity-section"
      className="relative bg-gradient-to-b from-cream/40 via-ivory to-cream/30 py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-20" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/8 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Compact image, smaller, lower */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="lg:col-span-4 relative max-w-sm mx-auto lg:mx-0"
          >
            <div className="relative aspect-square overflow-hidden gradient-border">
              <img
                src={IMAGES.necessity}
                alt="Loving family hands held together"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden md:block">
              <div className="bg-gold text-ink p-5 max-w-[180px] border border-ink">
                <div className="font-display text-3xl font-light leading-none">Zero</div>
                <div className="eyebrow text-ink/80 mt-2 text-[9px]">
                  GoFundMe campaigns needed for prepared families
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gold" />
              <span className="eyebrow text-gold">Why It Matters</span>
            </div>

            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
              An act of <span className="italic gold-text-gradient">love</span> — not a luxury.
            </h2>

            <div className="mt-10 space-y-6 text-ink/75 leading-relaxed text-base sm:text-lg font-light">
              <p>
                Life insurance isn't a product. It's a promise — the promise that{" "}
                <span className="text-ink font-medium">if something were to happen tomorrow</span>,
                the people who depend on you will still be okay. The mortgage gets paid. The kids
                stay in school. The lights stay on.
              </p>
              <p>
                Every week, families across America turn to{" "}
                <span className="text-gold font-medium">GoFundMe</span> to bury a loved one, to make
                rent, to keep the family afloat in the wake of devastating loss. It's a heartbreaking
                testament to a preventable problem.
              </p>
              <p>
                A properly structured policy{" "}
                <span className="text-ink font-medium">eliminates that desperation</span>. It
                transforms an unbearable tragedy into a protected legacy — giving your family the
                financial certainty to grieve in peace and the foundation to rebuild without
                crowdfunding their survival.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 sm:gap-10 pt-8 border-t border-line">
              <div>
                <div className="font-display font-light text-3xl sm:text-4xl text-gold">93%</div>
                <div className="eyebrow text-ink/55 mt-2 text-[10px]">
                  Of Americans say their family would suffer financially
                </div>
              </div>
              <div>
                <div className="font-display font-light text-3xl sm:text-4xl text-gold">$9k+</div>
                <div className="eyebrow text-ink/55 mt-2 text-[10px]">
                  Average cost of a funeral in the US
                </div>
              </div>
              <div>
                <div className="font-display font-light text-3xl sm:text-4xl text-gold">100%</div>
                <div className="eyebrow text-ink/55 mt-2 text-[10px]">
                  Tax-free benefit to your beneficiaries
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Necessity;
